import { useQueryClient } from "react-query";
import { axiosInstance } from "api/axios";
import useCustomQuery from "hooks/useCustomQuery";
import useCustomMutation from "hooks/useCustomMutation";
import {
  IProcurementNoticesResult,
  IProcurementQuery,
} from "dto/Procurement/IProcurementNotice";

// Poświadczenia klienta OAuth 2.0 (API MO — publikacja ogłoszeń).
export interface IProcurementCredential {
  IsConfigured: boolean;
  Environment?: string;
  ClientId?: string;
  /** Zamaskowany sekret (np. „••••XYZ9") — status, bez pełnego client_secret. */
  ClientSecretMask?: string;
  UpdatedAt?: string;
}

export interface ISaveProcurementCredential {
  Environment: string;
  ClientId: string;
  ClientSecret: string;
}

/**
 * Integracja z Platformą e-Zamówienia (Etap 1). Odczyt ogłoszeń o zamówieniach
 * publicznych z BZP WebService przez proxy BE (`/procurement/notices`) — bezpłatne,
 * bez uwierzytelnienia. Publikacja ogłoszeń/planów (OAuth 2.0) — Etap 2.
 */

const getNotices = async (
  params: IProcurementQuery,
): Promise<IProcurementNoticesResult> =>
  (await axiosInstance.get<IProcurementNoticesResult>("/procurement/notices", { params })).data;

const getCredentials = async (): Promise<IProcurementCredential> =>
  (await axiosInstance.get<IProcurementCredential>("/procurement/credentials")).data;

const saveCredentials = async (
  payload: ISaveProcurementCredential,
): Promise<IProcurementCredential> =>
  (await axiosInstance.post<IProcurementCredential>("/procurement/credentials", payload)).data;

const deleteCredentials = async (): Promise<void> => {
  await axiosInstance.delete("/procurement/credentials");
};

const Q_PROC_CREDS = "procurement.credentials";

const procurementService = {
  useNotices: (params: IProcurementQuery, enabled = true) =>
    useCustomQuery<IProcurementNoticesResult>(
      ["procurement.notices", params],
      () => getNotices(params),
      { enabled, keepPreviousData: true },
    ),

  /** Status poświadczeń publikacji (bez sekretu). */
  useCredentials: (enabled = true) =>
    useCustomQuery<IProcurementCredential>([Q_PROC_CREDS], getCredentials, { enabled }),

  useSaveCredentials: () => {
    const qc = useQueryClient();
    return useCustomMutation<IProcurementCredential, ISaveProcurementCredential>(
      saveCredentials,
      { onSuccess: () => qc.invalidateQueries(Q_PROC_CREDS) },
    );
  },

  useDeleteCredentials: () => {
    const qc = useQueryClient();
    return useCustomMutation<void, void>(
      () => deleteCredentials(),
      { onSuccess: () => qc.invalidateQueries(Q_PROC_CREDS) },
    );
  },
};

export default procurementService;
